import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { Column, DataTable } from "@/components/ui/data-table";
import { useSorting } from "@/hooks/useSorting";
import type { PartnerServiceListItem } from "@/types/partner-settings";
import { ServiceStatus } from "@/types/partner-settings";
import { cn } from "@/lib/utils";

export type GuesteriaServicesTableProps = {
  services: PartnerServiceListItem[];
  isLoading?: boolean | undefined;
};

function getStatusBadgeClass(status: ServiceStatus): string {
  switch (status) {
    case ServiceStatus.ACTIVE:
      return "bg-green-50 text-green-700";
    case ServiceStatus.INACTIVE:
      return "bg-gray-50 text-gray-700";
    default:
      return "bg-gray-50 text-gray-700";
  }
}

export const GuesteriaServicesTable = ({
  services,
  isLoading,
}: GuesteriaServicesTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { sortField, sortDirection, handleSort } = useSorting();

  const columns: Column<PartnerServiceListItem>[] = useMemo(
    () => [
      {
        key: "name",
        header: t("partners.services.table.name"),
        sortable: true,
        className: "flex-1",
        render: (_v, row) => (
          <div className="truncate">
            <p className="font-medium text-[#171717]">{row.name}</p>
            <p className="text-xs text-muted-foreground line-clamp-2">
              {row.description}
            </p>
          </div>
        ),
      },
      {
        key: "price",
        header: t("partners.services.table.price"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) =>
          row.price != null ? (
            <span className="text-sm font-medium text-[#171717]">
              €{row.price.toFixed(2)}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          ),
      },
      {
        key: "rating",
        header: t("partners.services.table.rating"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) =>
          row.rating != null ? (
            <div className="flex items-center justify-center gap-1">
              <Star className="size-3.5 fill-yellow-400 text-yellow-400" />
              <span className="text-sm font-medium text-[#171717]">
                {row.rating.toFixed(1)}
              </span>
              {row.reviewCount != null && (
                <span className="text-xs text-muted-foreground">
                  ({row.reviewCount})
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          ),
      },
      {
        key: "categories",
        header: t("partners.services.table.categories"),
        sortable: true,
        className: "w-[150px]",
        render: (_v, row) =>
          row.categories.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {row.categories.slice(0, 2).map((cat) => (
                <span
                  key={cat.tagId}
                  className="inline-block rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700"
                >
                  {cat.name}
                </span>
              ))}
              {row.categories.length > 2 && (
                <span className="text-xs text-muted-foreground">
                  +{row.categories.length - 2}
                </span>
              )}
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          ),
      },
      {
        key: "status",
        header: t("partners.services.table.status"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) => (
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
              getStatusBadgeClass(row.status),
            )}
          >
            {t(`partners.services.table.status.${row.status.toLowerCase()}`)}
          </span>
        ),
      },
      {
        key: "isRecommended",
        header: t("partners.services.table.recommended"),
        sortable: true,
        className: "w-[100px] text-center",
        render: (_v, row) =>
          row.isRecommended ? (
            <span className="text-sm font-medium text-green-700">
              {t("common.yes")}
            </span>
          ) : (
            <span className="text-sm text-muted-foreground">
              {t("common.no")}
            </span>
          ),
      },
    ],
    [t],
  );

  const sortedServices = useMemo(() => {
    if (!sortField || !sortDirection) {
      return services;
    }

    const sorted = [...services].sort((a, b) => {
      let aVal: unknown;
      let bVal: unknown;

      if (sortField === "categories") {
        aVal = a.categories[0]?.name ?? "";
        bVal = b.categories[0]?.name ?? "";
      } else {
        aVal = (a as unknown as Record<string, unknown>)[sortField];
        bVal = (b as unknown as Record<string, unknown>)[sortField];
      }

      if (aVal === null || aVal === undefined) aVal = "";
      if (bVal === null || bVal === undefined) bVal = "";

      let comparison = 0;
      if (typeof aVal === "string" && typeof bVal === "string") {
        comparison = aVal.localeCompare(bVal);
      } else if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else if (typeof aVal === "boolean" && typeof bVal === "boolean") {
        comparison = Number(aVal) - Number(bVal);
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });

    return sorted;
  }, [services, sortField, sortDirection]);

  return (
    <DataTable<PartnerServiceListItem>
      data={sortedServices}
      columns={columns}
      getRowKey={(row) => row.id}
      columnHeaderClassName="text-xs font-semibold bg-gray-50"
      sorting={{
        field: sortField,
        direction: sortDirection,
        onSort: handleSort,
      }}
      emptyMessage={t("partners.services.table.empty")}
      loading={isLoading}
    />
  );
};
